import {
  DEPARTMENTS,
  CLASSES,
  SUBCLASSES,
  SUBCLASSWALLPAPER
} from '../constants';

const BUYABLE_FLOOR_CALCULATOR = 'buyableFloorCalculator';
const SCHEDULE_FLOOR_CALCULATOR = 'scheduleFloorCalculator';
const CONFIGURABLE_CALCULATOR = 'configurableCalculator';

const groups = {
  FUNCTIONAL_DETAILS: 'Details',
  SUPPLEMENTAL_DIMENSIONS: 'Dimensions'
};

const guids = {
  BAG_CAPACITY_DRY_VOLUME: 'Bag Capacity/Dry Volume (cu. ft.)',
  PRODUCT_WIDTH_FEET: 'Product Width (ft.)',
  PRODUCT_LENGTH_FEET: 'Product Length (ft.)',
  SQUARE_FEET_PER_PIECE: 'Square Feet per Piece',
  COVERAGE_AREA_SQUARE_FEET: 'Coverage Area (sq. ft.)'
};

export const displayUOM = (uom) => {
  const uomMap = {
    each: '',
    pallet: 'pallet'
  };

  const arr = Object.keys(uomMap).filter(function (uomKey) {
    return uom.indexOf(uomKey) > -1;
  });

  return arr.length ? uomMap[arr[0]] : uom.toLowerCase();
};

export const pluralizeUnitOfMeasure = (uom) => {
  if (!uom || uom === 'each') {
    return '';
  }
  // handle special cases, such as pallet(lift) here
  const uomMap = {
    'square yard': 'square yards',
    case: 'cases',
    pallet: 'pallets',
    'square foot': 'square feet'
  };
  const matchingUoms = Object.keys(uomMap).filter(function (uomKey) {
    return uom.indexOf(uomKey) > -1;
  });
  if (matchingUoms.length) { // found a match, return the special case
    return uomMap[matchingUoms[0]];
  }

  // handle other cases
  if (uom.length > 1) {
    const lastChar = uom[uom.length - 1];
    const lastTwoChars = uom.substring(uom.length - 2);

    if ('sxz'.indexOf(lastChar.toLowerCase()) !== -1
      || ['sh', 'ch'].indexOf(lastTwoChars.toLowerCase()) !== -1) {

      return uom + 'es';
    }
  }

  return uom + 's';
};

export const shouldDisplayFloorWallpaperCalculator = ({ pricing, info }) => {
  const alternatePricing = pricing?.alternatePriceDisplay || false;
  const { projectCalculatorEligible, productDepartment, classNumber, subClassNumber } = info;
  const departmentExist = DEPARTMENTS.indexOf(parseInt(productDepartment, 10));
  const classExist = CLASSES.indexOf(parseInt(classNumber, 10));
  const subClassExist = SUBCLASSES.indexOf(parseInt(subClassNumber, 10));

  if (projectCalculatorEligible && alternatePricing) {
    return true;
  }

  return !!(!projectCalculatorEligible
    && departmentExist > -1
    && classExist > -1
    && subClassExist > -1
    && alternatePricing);

};

// const shouldDisplayFlooringCalculator = ({ pricing }) => {
//   return !!pricing?.alternate?.unit?.unitsPerCase;
// };

export const shouldDisplayConfigurableCalculator = ({ info = {} }) => {
  return !!info?.calculatorType;
};

export const shouldDisplayScheduleCalculator = ({ installServices, pricing }) => {
  return installServices?.scheduleAMeasure
    && pricing?.value;
};

export const shouldDisplayQuantityCalculator = ({ fulfillment, installServices, pricing, info }) => {
  return !!(fulfillment?.fulfillmentOptions
    && !installServices?.scheduleAMeasure
    && (shouldDisplayFloorWallpaperCalculator({ pricing, info }) || shouldDisplayConfigurableCalculator({ info })));
};

export const shouldDisplayLegacyCalculator = ({ pricing }) => {
  return pricing?.alternatePriceDisplay;
};

export const shouldDisplayCalculator = ({ pricing, info }) => {
  return (shouldDisplayLegacyCalculator({ pricing }) || shouldDisplayConfigurableCalculator({ info }));
};

export const getCalculatorType = ({ fulfillment, installServices, pricing, info }) => {
  if (shouldDisplayQuantityCalculator({ fulfillment, installServices, pricing, info })) {
    if (shouldDisplayConfigurableCalculator({ info })) {
      return CONFIGURABLE_CALCULATOR;
    }
    if (shouldDisplayFloorWallpaperCalculator({ pricing, info })) {
      return BUYABLE_FLOOR_CALCULATOR;
    }
  }

  if (shouldDisplayScheduleCalculator({ installServices, pricing })) {
    return SCHEDULE_FLOOR_CALCULATOR;
  }

  return 'unknownCalculatorType';
};

export const getSpecificationGroupValue = (specificationGroup, group, specName) => {
  const specGroup = (specificationGroup || []).find((grp) => grp.specTitle === group);
  const attribute = (specGroup?.specifications || []).find((specification) => specification.specName === specName);
  return attribute?.specValue.split(' ')[0];
};

export const getCompositeCoverage = (specificationGroup, attributeGroupLookups) => {
  const attributes = attributeGroupLookups.map((lookup) => {
    return getSpecificationGroupValue(specificationGroup, lookup.group, lookup.specName);
  }).filter((attribute) => {
    return attribute;
  });
  if (attributes.length !== attributeGroupLookups.length) {
    return null;
  }
  return attributes.reduce((product, attribute) => {
    return parseInt(product, 10) * parseInt(attribute, 10);
  }, 1);
};

export const getConfigurableProps = ({ info, pricing, specificationGroup }) => {
  const calculatorType = info?.calculatorType;
  switch (calculatorType) {
    case 'Flooring':
      return {
        calculatorType: 'Area',
        lineItemName: 'Area',
        dimensionNames: ['Length', 'Width'],
        uom: displayUOM(pricing?.unitOfMeasure),
        uomPlural: pluralizeUnitOfMeasure(pricing?.unitOfMeasure),
        caseUnitOfMeasure: pricing?.alternate?.unit?.caseUnitOfMeasure,
        productCategory: 'product',
        unitsPerCase: pricing?.alternate?.unit?.unitsPerCase
      };
    case 'Mulch/Soil':
      return {
        calculatorType: 'Volume',
        lineItemName: 'Space',
        dimensionNames: ['Length', 'Width'],
        uom: 'bag',
        uomPlural: 'bags',
        caseUnitOfMeasure: 'cu. ft.',
        productCategory: 'mulch/soil',
        unitsPerCase: getSpecificationGroupValue(
          specificationGroup,
          groups.FUNCTIONAL_DETAILS,
          guids.BAG_CAPACITY_DRY_VOLUME
        )
      };
    case 'Landscape Rock':
      return {
        calculatorType: 'Volume',
        lineItemName: 'Space',
        dimensionNames: ['Length', 'Width'],
        uom: 'bag',
        uomPlural: 'bags',
        caseUnitOfMeasure: 'cu. ft.',
        productCategory: 'landscape rock',
        unitsPerCase: getSpecificationGroupValue(
          specificationGroup,
          groups.FUNCTIONAL_DETAILS,
          guids.BAG_CAPACITY_DRY_VOLUME
        )
      };
    case 'Drywall':
      return {
        calculatorType: 'Area',
        lineItemName: 'Wall',
        dimensionNames: ['Width', 'Length'],
        uom: 'sheet',
        uomPlural: 'sheets',
        caseUnitOfMeasure: 'sq. ft.',
        productCategory: 'drywall',
        unitsPerCase: getCompositeCoverage(
          specificationGroup,
          [
            { group: groups.SUPPLEMENTAL_DIMENSIONS, specName: guids.PRODUCT_WIDTH_FEET },
            { group: groups.SUPPLEMENTAL_DIMENSIONS, specName: guids.PRODUCT_LENGTH_FEET }
          ]
        )
      };
    case 'Wall Block':
      return {
        calculatorType: 'Area',
        lineItemName: 'Wall',
        dimensionNames: ['Width', 'Height'],
        uom: 'block',
        uomPlural: 'blocks',
        caseUnitOfMeasure: 'sq. ft.',
        productCategory: 'product',
        unitsPerCase: getSpecificationGroupValue(
          specificationGroup,
          groups.FUNCTIONAL_DETAILS,
          guids.SQUARE_FEET_PER_PIECE
        )
      };
    case 'Pavers':
      return {
        calculatorType: 'Area',
        lineItemName: 'Area',
        dimensionNames: ['Length', 'Width'],
        uom: 'piece',
        uomPlural: 'pieces',
        caseUnitOfMeasure: 'sq. ft.',
        productCategory: 'product',
        unitsPerCase: getSpecificationGroupValue(
          specificationGroup,
          groups.FUNCTIONAL_DETAILS,
          guids.SQUARE_FEET_PER_PIECE
        )
      };
    case 'Siding':
      return {
        calculatorType: 'Area',
        lineItemName: 'Wall',
        dimensionNames: ['Length', 'Width'],
        uom: 'panel',
        uomPlural: 'panels',
        caseUnitOfMeasure: 'sq. ft.',
        productCategory: 'siding',
        unitsPerCase: getSpecificationGroupValue(
          specificationGroup,
          groups.SUPPLEMENTAL_DIMENSIONS,
          guids.COVERAGE_AREA_SQUARE_FEET
        )
      };
    case 'Insulation':
      return {
        calculatorType: 'Area',
        lineItemName: 'Area',
        uom: 'unit',
        uomPlural: 'units',
        dimensionNames: ['Length', 'Width'],
        caseUnitOfMeasure: 'sq. ft.',
        productCategory: 'insulation',
        unitsPerCase: getSpecificationGroupValue(
          specificationGroup,
          groups.SUPPLEMENTAL_DIMENSIONS,
          guids.COVERAGE_AREA_SQUARE_FEET
        )
      };
    case 'Coverage':
      return {
        calculatorType: 'Area',
        lineItemName: 'Area',
        uom: 'unit',
        uomPlural: 'units',
        dimensionNames: ['Length', 'Width'],
        caseUnitOfMeasure: 'sq. ft.',
        productCategory: 'product',
        unitsPerCase: getSpecificationGroupValue(
          specificationGroup,
          groups.SUPPLEMENTAL_DIMENSIONS,
          guids.COVERAGE_AREA_SQUARE_FEET
        )
      };
    default:
      return null;
  }
};

export const getCalculatorProps = ({
  pricing,
  fulfillment,
  installServices,
  info,
  specificationGroup
}) => {
  const calculatorType = getCalculatorType({ fulfillment, installServices, pricing, info });
  // Flooring or Wallpaper Calculator
  if (calculatorType === BUYABLE_FLOOR_CALCULATOR) {
    const wallpaperCalc = info?.subClassNumber === SUBCLASSWALLPAPER;
    return {
      calculatorType: 'Area',
      lineItemName: wallpaperCalc ? 'wall' : 'Room',
      dimensionNames: wallpaperCalc ? ['Width', 'Height'] : ['Length', 'Width'],
      uom: displayUOM(pricing?.unitOfMeasure),
      uomPlural: pluralizeUnitOfMeasure(pricing?.unitOfMeasure),
      caseUnitOfMeasure: pricing?.alternate?.unit?.caseUnitOfMeasure,
      productCategory: wallpaperCalc ? 'product' : 'flooring',
      unitsPerCase: pricing?.alternate?.unit?.unitsPerCase
    };
  }

  // Schecule Floor Calculator
  if (calculatorType === SCHEDULE_FLOOR_CALCULATOR) {
    return {
      calculatorType: 'Area',
      lineItemName: 'Area',
      dimensionNames: ['Length', 'Width'],
      uom: displayUOM(pricing?.unitOfMeasure),
      uomPlural: pluralizeUnitOfMeasure(pricing?.unitOfMeasure),
      caseUnitOfMeasure: pricing?.alternate?.unit?.caseUnitOfMeasure,
      productCategory: 'flooring',
      unitsPerCase: pricing?.alternate?.unit?.unitsPerCase
    };
  }

  // Configurable Calculator
  if (calculatorType === CONFIGURABLE_CALCULATOR) {
    return getConfigurableProps({ pricing, info, specificationGroup });
  }
  return null;
};